/**
 * Cross-vendor inventory store.
 *
 * Today this is a single API call (`/devices/`) that the backend will
 * eventually fan out to each vendor; for the migration period it's served
 * from the synthesized cross-vendor fixture so the page is fully clickable.
 *
 * Filters live here so deep-linking from a vendor pill keeps working when
 * we add URL syncing later.
 */
import { create } from "zustand";
import { get as apiGet, ApiError } from "@/api/client";
import {
  InventoryDeviceListSchema,
  type InventoryDevice,
  type DeviceVendor,
  type DeviceStatus,
} from "@/api/schemas/devices";
import { MOCK_INVENTORY_DEVICES } from "@/api/fixtures/devices";

export interface InventoryFilters {
  query: string;
  vendors: DeviceVendor[];
  statuses: DeviceStatus[];
}

const EMPTY_FILTERS: InventoryFilters = { query: "", vendors: [], statuses: [] };

interface State {
  data: InventoryDevice[];
  filters: InventoryFilters;
  status: "idle" | "loading" | "ready" | "error";
  error: string | null;

  load: () => Promise<void>;
  setFilters: (patch: Partial<InventoryFilters>) => void;
  resetFilters: () => void;
}

export const useInventory = create<State>((set, get) => ({
  data: [],
  filters: EMPTY_FILTERS,
  status: "idle",
  error: null,

  load: async () => {
    if (get().status === "loading") return;
    set({ status: "loading", error: null });
    try {
      const raw = await apiGet<unknown>("/devices/");
      set({ data: InventoryDeviceListSchema.parse(raw), status: "ready" });
    } catch (err) {
      const message =
        err instanceof ApiError
          ? `API error ${err.status}`
          : err instanceof Error
            ? err.message
            : "unknown error";
      set({
        data: MOCK_INVENTORY_DEVICES,
        status: "error",
        error: `${message} — showing fixture data`,
      });
    }
  },

  setFilters: (patch) => set((s) => ({ filters: { ...s.filters, ...patch } })),
  resetFilters: () => set({ filters: EMPTY_FILTERS }),
}));
